import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function PregnancyDialog({ open, onOpenChange, cattle, bulls, onSave, checkRelation }) {
  const [formData, setFormData] = useState({
    reproductionType: 'natural',
    fatherId: '',
    fatherDocument: null,
    conceptionDate: '',
    notes: ''
  });
  const [fileName, setFileName] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Solo se permiten imágenes (JPG, PNG) o PDF');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error('El archivo no debe superar 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          fatherDocument: {
            data: reader.result,
            name: file.name,
            type: file.type
          }
        }));
        setFileName(file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateExpectedDate = (conceptionDate) => {
    if (!conceptionDate) return '';
    const conception = new Date(conceptionDate);
    conception.setDate(conception.getDate() + 283); // ~9 meses de gestación
    return conception.toISOString().split('T')[0];
  };

  const handleSubmit = () => {
    if (!formData.conceptionDate) {
      toast.error('Debe indicar la fecha de concepción');
      return;
    }

    if (formData.reproductionType === 'natural' && !formData.fatherId) {
      toast.error('Debe seleccionar el toro padre');
      return;
    }

    // Validate against inbreeding
    if (formData.reproductionType === 'natural' && checkRelation && checkRelation(formData.fatherId)) {
      toast.error('No se puede seleccionar este toro porque es padre o abuelo de esta vaca');
      return;
    }

    if (formData.reproductionType === 'artificial' && !formData.fatherDocument) {
      toast.error('Debe subir documento del padre (pajilla de inseminación)');
      return;
    }

    const pregnancyData = {
      cattleId: cattle.id,
      ...formData,
      expectedDate: calculateExpectedDate(formData.conceptionDate)
    };

    onSave(pregnancyData);
    onOpenChange(false);
    
    // Reset form
    setFormData({
      reproductionType: 'natural',
      fatherId: '',
      fatherDocument: null,
      conceptionDate: '',
      notes: ''
    });
    setFileName('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registrar Embarazo - {cattle?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tipo de Reproducción */}
          <div className="space-y-2">
            <Label>Tipo de Reproducción</Label>
            <Select 
              value={formData.reproductionType} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, reproductionType: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="natural">Monta Natural (Toro)</SelectItem>
                <SelectItem value="artificial">Inseminación Artificial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selección de Padre - Monta Natural */}
          {formData.reproductionType === 'natural' && (
            <div className="space-y-2">
              <Label>Toro Padre</Label>
              <Select 
                value={formData.fatherId} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, fatherId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar toro..." />
                </SelectTrigger>
                <SelectContent>
                  {bulls.length > 0 ? (
                    bulls.map(bull => (
                      <SelectItem key={bull.id} value={bull.id}>
                        {bull.name} ({bull.tag}) - {bull.breed}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No hay toros registrados</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se creará un control cruzado entre la vaca y el toro seleccionado
              </p>
            </div>
          )}

          {/* Upload de Documento - Inseminación */}
          {formData.reproductionType === 'artificial' && (
            <div className="space-y-2">
              <Label>Documento del Padre (Pajilla/Certificado)</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-rose-400 transition-colors">
                <input
                  type="file"
                  id="father-doc"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <label htmlFor="father-doc" className="cursor-pointer">
                  {fileName ? (
                    <div className="space-y-2">
                      <FileText className="h-12 w-12 mx-auto text-rose-600" />
                      <p className="text-sm font-medium">{fileName}</p>
                      <p className="text-xs text-muted-foreground">Click para cambiar</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                      <p className="text-sm font-medium">Subir documento</p>
                      <p className="text-xs text-muted-foreground">
                        Imagen (JPG, PNG) o PDF - Máx. 5MB
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Fecha de Concepción */}
          <div className="space-y-2">
            <Label>Fecha Aproximada de Concepción</Label>
            <Input
              type="date"
              value={formData.conceptionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, conceptionDate: e.target.value }))}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Fecha Esperada de Parto (calculada) */}
          {formData.conceptionDate && (
            <div className="space-y-2">
              <Label>Fecha Esperada de Parto (Calculada)</Label>
              <Input
                type="date"
                value={calculateExpectedDate(formData.conceptionDate)}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Basado en período de gestación de ~283 días
              </p>
            </div>
          )}

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas (Opcional)</Label>
            <Textarea
              placeholder="Observaciones adicionales sobre la reproducción..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="bg-rose-600 hover:bg-rose-700">
            Registrar Embarazo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
